/**
 * Handler registry.
 *
 * The registry is also where the catalog and the code are reconciled: a format
 * the catalog advertises as editable but whose handler has no `exportBytes` is
 * a §19 violation, and {@link auditRegistry} reports it rather than letting the
 * product ship an Edit button that cannot save.
 */
import { FORMATS_BY_ID, canEdit, canView } from '@moduviewer/core';
import type { FormatHandler } from './contract.js';

export interface RegistryAudit {
  /** Catalog says editable, handler cannot export. */
  readonly claimsEditWithoutExport: readonly string[];
  /** Handler can export, catalog says it cannot be edited. */
  readonly exportsWithoutClaim: readonly string[];
  /** Handler registered for an id that is not in the catalog. */
  readonly unknownFormatIds: readonly string[];
  /** Catalog says viewable, no handler exists yet. */
  readonly viewableWithoutHandler: readonly string[];
  /** Two handlers registered for the same format. */
  readonly duplicates: readonly string[];
}

export class HandlerRegistry {
  readonly #byFormat = new Map<string, FormatHandler<never>>();
  readonly #handlers: FormatHandler<never>[] = [];

  register<TModel>(handler: FormatHandler<TModel>): this {
    this.#handlers.push(handler as unknown as FormatHandler<never>);
    for (const id of handler.formatIds) {
      if (!this.#byFormat.has(id)) {
        this.#byFormat.set(id, handler as unknown as FormatHandler<never>);
      }
    }
    return this;
  }

  /** The handler for a format, or null when none is registered. */
  get(formatId: string): FormatHandler<never> | null {
    return this.#byFormat.get(formatId) ?? null;
  }

  has(formatId: string): boolean {
    return this.#byFormat.has(formatId);
  }

  /** True when the format can actually be edited *and saved* right now. */
  canEditFormat(formatId: string): boolean {
    const handler = this.#byFormat.get(formatId);
    if (!handler?.exportBytes) return false;
    const spec = FORMATS_BY_ID.get(formatId);
    return spec !== undefined && canEdit(spec.edit);
  }

  get handlers(): readonly FormatHandler<never>[] {
    return this.#handlers;
  }

  get formatIds(): readonly string[] {
    return [...this.#byFormat.keys()];
  }

  /**
   * Compare what the catalog promises against what the handlers can do.
   *
   * Run as a test rather than at startup: a mismatch is a development mistake
   * to be caught before release, not a runtime condition to recover from.
   */
  audit(): RegistryAudit {
    const claimsEditWithoutExport: string[] = [];
    const exportsWithoutClaim: string[] = [];
    const unknownFormatIds: string[] = [];
    const duplicates: string[] = [];
    const seen = new Set<string>();

    for (const handler of this.#handlers) {
      for (const id of handler.formatIds) {
        if (seen.has(id)) duplicates.push(id);
        seen.add(id);

        const spec = FORMATS_BY_ID.get(id);
        if (!spec) { unknownFormatIds.push(id); continue; }

        const catalogEditable = canEdit(spec.edit);
        const handlerEditable = handler.exportBytes !== undefined;

        // A `convert-edit` format is edited by converting to another format — a
        // separate capability — so a handler may legitimately open it for
        // reading with no `exportBytes` (RTF), *or* implement the conversion as
        // an export (an image saved as PNG). Both are honest, so convert-edit is
        // never a missing-export violation…
        if (catalogEditable && spec.edit !== 'convert-edit' && !handlerEditable) {
          claimsEditWithoutExport.push(id);
        }
        // …and having an export for it is not an unclaimed-edit violation
        // either, because the catalog does mark it editable.
        if (handlerEditable && !catalogEditable) exportsWithoutClaim.push(id);
      }
    }

    const viewableWithoutHandler = [...FORMATS_BY_ID.values()]
      .filter((spec) => canView(spec.view) && !this.#byFormat.has(spec.id))
      .map((spec) => spec.id);

    return {
      claimsEditWithoutExport, exportsWithoutClaim,
      unknownFormatIds, viewableWithoutHandler, duplicates,
    };
  }
}
